import React, { useState, useContext, useEffect } from "react";
import { GlobalContext } from "../context/GlobalState";
import { css } from "emotion";
import { boxStyles, colors, textSizes } from "../styles/theme";
import SwitchHeader from "../components/SwitchHeader";
import TitleBlock from "../components/TitleBlock";
import GenericButton from "../components/GenericButton";
import auth from "../services/api/auth";
import { withRouter, Link, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import ErrorMessage from "../components/ErrorMessage";

const LogIn = ({ history }) => {
  useEffect(() => {
    document.title = "Login - Spotlight";
  });

  const { doLogin, currentWorkspace } = useContext(GlobalContext);
  const methods = useForm();
  const { register, handleSubmit, errors, setError } = methods;
  const [isLoading, setLoading] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const query = new URLSearchParams(useLocation().search);
  const coupon = query.get("coupon");

  if (coupon && currentWorkspace && !hasCheckedAuth) {
    setHasCheckedAuth(true);
    history.push(`/home?coupon=${coupon}`);
  } else if (currentWorkspace && !hasCheckedAuth) {
    setHasCheckedAuth(true);
    history.push("/home");
  }

  if (coupon) {
    sessionStorage.setItem("coupon", coupon);
  }

  const handleLoginClick = async ({ email, password }) => {
    setLoading(true);
    const loginData = {
      emailAddress: email,
      password,
    };

    try {
      const { data } = await auth.logIn(loginData);
      // console.log("DATA", data);

      data.defaultWorkspace.spotlights = [];

      const stateData = {
        userData: {
          id: data.id,
          emailAddress: data.emailAddress,
          firstName: data.firstName,
          lastName: data.lastName,
          invites: data.invites,
          isAdmin: data.isAdmin,
          roles: data.roles,
        },
        defaultWorkspace: data.defaultWorkspace,
        workspaces: data.workspaces,
      };

      doLogin(stateData);
      history.push("/home");
    } catch (error) {
      if (error.response) {
        setError(
          "login",
          "badCombo",
          "This email and password combination do not match. Please double check and try again."
        );
      } else if (error.request) {
        setError(
          "login",
          "noConnection",
          "You seem to have gotten disconnected. Make sure your internet is connected and try again."
        );
        console.log(error.request);
      } else {
        setError(
          "login",
          "unexpected",
          "There was a problem logging into your account. Please reload the page and try again."
        );
        console.log("Error", error.message);
      }
      setLoading(false);
    }
  };

  return (
    <div className={container}>
      <SwitchHeader />
      <div className="body">
        <div className="centerBlock">
          <div className="hero">
            <TitleBlock
              title="Welcome back,"
              subtitle="Log in below to get started."
            />
          </div>
          <form className="form">
            {errors.login && (
              <ErrorMessage>{errors.login.message}</ErrorMessage>
            )}
            <div className="formItem">
              <label>
                <p>EMAIL</p>
                <input
                  name="email"
                  type="email"
                  ref={register({
                    required: {
                      value: true,
                      message: "Email is required",
                    },
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
              </label>
              {errors.email && (
                <ErrorMessage>{errors.email.message}</ErrorMessage>
              )}
            </div>
            <div className="formItem">
              <label>
                <p style={{ display: "inline-block" }}>PASSWORD</p>
                <input
                  name="password"
                  type="password"
                  ref={register({
                    required: {
                      value: true,
                      message: "Password is required",
                    },
                  })}
                />
                <Link to="/forgot-password" className="forgot-password-link">
                  Forget your password?
                </Link>
              </label>
              {errors.password && (
                <ErrorMessage>{errors.password.message}</ErrorMessage>
              )}
            </div>
            <div className="buttonSection">
              <div className="button">
                <GenericButton
                  text="Log In"
                  handleClick={handleSubmit(handleLoginClick)}
                  disabled={isLoading}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default withRouter(LogIn);

const container = css`
  display: flex;
  flex-direction: column;

  .body {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .centerBlock {
    max-width: 650px;
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
  }

  .hero {
    max-width: 650px;
    width: 100%;
    padding: 40px 0;
  }
  .form {
    width: 90%;
    max-width: 455px;

    .buttonSection {
      display: flex;
      flex: 1;
      justify-content: center;
      padding: 10px;
      margin-top: 24px;

      .button {
        width: 50%;
      }
      button:focus {
        outline: none;
      }
    }
  }
  .formItem {
    padding: 10px;

    label {
      font-size: ${textSizes.normal};
      color: ${colors.placeholderText};

      input {
        color: ${colors.inputTextColor};
        box-sizing: border-box;
        width: 100%;
        background-color: ${colors.inputBackground};
        padding: 10px;
        border: none;
        border-radius: ${boxStyles.borderRadius};
        color: ${colors.inputTextColor};
        font-size: ${textSizes.normal};
        outline: none;
      }
    }
  }

  .passwordInput {
    p {
      bottom: 10px;
      right: 10px;
    }
  }

  .forgot-password-link {
    display: inline-block;
    margin-left: 5px;
    color: ${colors.placeholderText};
    text-decoration: none;
    opacity: 0.7;
    transition-duration: 0.2s;
    padding: 5px 0;

    &:hover {
      opacity: 1;
    }
  }
`;
